import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, apiError, formatPhone } from "@/lib/api";
import { useUnsavedChanges } from "@/lib/useUnsavedChanges";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, Star, Upload, Save, MailX, Send, X, Loader2, Eye, Sparkles, Linkedin, Facebook, Twitter, Instagram, Youtube, Github, Globe } from "lucide-react";
import ResearchDialog from "@/components/ResearchDialog";
import OptOutCautionDialog from "@/components/OptOutCautionDialog";
import { toast } from "sonner";
import RecordNav from "@/components/RecordNav";
import { Switch } from "@/components/ui/switch";

const uid = () => Math.random().toString(36).slice(2);

export const SOCIAL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/in/username" },
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/username" },
  { key: "twitter", label: "X / Twitter", icon: Twitter, placeholder: "https://x.com/username" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/username" },
  { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@channel" },
  { key: "github", label: "GitHub", icon: Github, placeholder: "https://github.com/username" },
  { key: "website", label: "Website", icon: Globe, placeholder: "https://example.com" },
];

const emptySocial = { linkedin: "", facebook: "", twitter: "", instagram: "", youtube: "", github: "", website: "" };

const empty = {
  prefix: "", first_name: "", middle_name: "", last_name: "", suffix: "", preferred_name: "", former_name: "", birthdate: "",
  addressee: "", salutation: "", solicit_code: "", last_gift_date: "",
  class_year: "", year_graduated: "", year_entered: "", photo: "", living_status: "living", profile_status: "unclaimed",
  emails: [], phones: [], addresses: [], degrees: [], employments: [],
  interests: [], membership_status: "none", membership_since: "", membership_expires: "",
  family: [], giving: [], events: [], communications: [], social: { ...emptySocial },
};

export default function AlumniForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const agentCtx = user?.role === "class_agent" || !!user?.impersonated;
  const base = agentCtx ? "/agent/alumni" : "/alumni";
  const isClassAgent = agentCtx;
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [commTemplates, setCommTemplates] = useState([]);
  const [sendTo, setSendTo] = useState(null); // email string when the picker is open
  const [sentNote, setSentNote] = useState(null); // { title, date } after a send from this form
  const [research, setResearch] = useState(false);

  useEffect(() => {
    if (!id || agentCtx) return;
    api.get("/comm-templates").then((r) => setCommTemplates(r.data?.templates || [])).catch(() => {});
  }, [id, agentCtx]);

  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

  const savedSnapshot = useRef("");
  useEffect(() => {
    if (id) {
      if (!user) return; // wait until role/impersonation resolves so `base` is correct
      api.get(`${base}/${id}`).then((r) => {
        const loaded = { ...empty, ...r.data, class_year: r.data.class_year ?? "", year_graduated: r.data.year_graduated ?? "", year_entered: r.data.year_entered ?? "", social: { ...emptySocial, ...(r.data.social || {}) } };
        setF(loaded);
        savedSnapshot.current = JSON.stringify(loaded);
      });
    } else {
      savedSnapshot.current = JSON.stringify(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, base, user]);

  const dirty = savedSnapshot.current !== "" && JSON.stringify(f) !== savedSnapshot.current;
  const guard = useUnsavedChanges(dirty);

  const derivedLastSent = (() => {
    const sent = (f.communications || []).filter((c) => (c.notes || "").startsWith("Sent:"));
    if (!sent.length) return null;
    const last = sent.slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];
    return { title: (last.notes || "").replace(/^Sent:\s*/, "") || last.subject || "a communication", date: last.date || "" };
  })();
  const showLastSent = sentNote || derivedLastSent;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setF((p) => ({ ...p, social: { ...p.social, [k]: v } }));

  const addItem = (key, obj) => setF((p) => ({ ...p, [key]: [...p[key], { id: uid(), primary: p[key].length === 0, ...obj }] }));
  const updItem = (key, i, patch) => setF((p) => ({ ...p, [key]: p[key].map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const delItem = (key, i) => setF((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));
  const setPrimary = (key, i) => setF((p) => ({ ...p, [key]: p[key].map((it, idx) => ({ ...it, primary: idx === i })) }));

  const [cautionPatch, setCautionPatch] = useState(null);
  // Only opting OUT (v === true) triggers the caution modal; opting back in is immediate.
  const requestOptOut = (patch, v) => { if (v) setCautionPatch(patch); else updateEmailPrefs(patch); };

  const updateEmailPrefs = async (patch) => {
    try {
      const { data } = await api.patch(`/alumni/${id}/email-preferences`, patch);
      setF((p) => ({ ...p, email_opt_out: data.email_opt_out, email_prefs: data.email_prefs }));
      try { const snap = JSON.parse(savedSnapshot.current || "{}"); snap.email_opt_out = data.email_opt_out; snap.email_prefs = data.email_prefs; savedSnapshot.current = JSON.stringify(snap); } catch {}
      toast.success("Email preferences updated");
    } catch (e) {
      toast.error("Could not update email preferences");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.path);
      toast.success("Photo uploaded");
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!f.first_name && !f.last_name) { toast.error("First or last name is required"); return; }
    setBusy(true);
    const payload = { ...f, class_year: f.class_year ? parseInt(f.class_year) : null,
      year_graduated: f.year_graduated ? parseInt(f.year_graduated) : null,
      year_entered: f.year_entered ? parseInt(f.year_entered) : null,
      degrees: f.degrees.map((d) => ({ ...d, grad_year: d.grad_year ? parseInt(d.grad_year) : null })),
      giving: f.giving.map((g) => ({ ...g, amount: g.amount !== "" && g.amount != null ? parseFloat(g.amount) : null })) };
    try {
      savedSnapshot.current = JSON.stringify(f);
      if (id) {
        await api.put(`${base}/${id}`, payload);
        toast.success("Profile updated");
        navigate(isClassAgent ? `/agent/alumni/${id}` : `/alumni/${id}`);
      } else {
        const { data } = await api.post("/alumni", payload);
        toast.success("Alumnus created");
        navigate(`/alumni/${data.id}`);
      }
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up max-w-4xl">
      <button onClick={() => { if (!guard()) return; try { const raw = sessionStorage.getItem("ud_record_return"); if (raw) { const ctx = JSON.parse(raw); if (ctx && Array.isArray(ctx.ids) && ctx.ids.includes(id) && ctx.path) { sessionStorage.removeItem("ud_record_return"); navigate(ctx.path); return; } } } catch {} navigate(-1); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-3xl tracking-tight">{id ? "Edit Alumnus" : "Add Alumnus"}</h1>
          {!(f.emails || []).some((e) => (e.value || "").trim()) && (
            <span data-testid="email-missing-pill" className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200" title="No email address on file for this alumnus">
              <MailX className="h-3.5 w-3.5" /> Email missing
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {id && <button data-testid="form-research-button" type="button" onClick={() => setResearch(true)} className="flex items-center gap-1.5 text-sm text-primary border border-primary rounded-md px-3 py-1.5 hover:bg-primary/5 transition-colors"><Sparkles className="h-4 w-4" /> Research Alumnus</button>}
          {id && <RecordNav id={id} mode="edit" canLeave={guard} basePath={base} />}
          <button data-testid="save-alumnus" onClick={save} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Card title="Basic Information">
        <div className="flex items-center gap-4 mb-4">
          {f.photo ? <img src={fileUrl(f.photo)} alt="" className="h-20 w-20 rounded-full object-cover" /> : <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs">No photo</div>}
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-muted text-sm">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : (f.photo ? "Replace Photo" : "Upload Photo")}
            <input data-testid="photo-upload" type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
          {f.photo && (
            <button
              type="button"
              data-testid="photo-remove"
              onClick={() => { set("photo", ""); toast.success("Photo removed — click Save to apply"); }}
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-border text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="h-4 w-4" /> Remove
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name" k="first_name" value={f.first_name} onChange={set} />
          <Field label="Last Name" k="last_name" value={f.last_name} onChange={set} />
          <Field label="Middle Name" k="middle_name" value={f.middle_name} onChange={set} />
          <div>
            <label className="block text-sm font-medium mb-1">Suffix</label>
            <select data-testid="field-suffix" value={f.suffix || ""} onChange={(e) => set("suffix", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              {["", "Jr.", "Sr.", "II", "III", "IV", "V"].map((s) => <option key={s} value={s}>{s || "— None —"}</option>)}
            </select>
          </div>
          <Field label="Preferred Name" k="preferred_name" value={f.preferred_name} onChange={set} />
          <Field label="Former / Maiden Name" k="former_name" value={f.former_name} onChange={set} />
          <div>
            <label className="block text-sm font-medium mb-1">Birthdate</label>
            <div className="flex items-center gap-2">
              <input data-testid="field-birthdate" type="date" value={f.birthdate || ""} onChange={(e) => set("birthdate", e.target.value)} className="flex-1 px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
              {f.birthdate && <button type="button" data-testid="birthdate-clear" onClick={() => set("birthdate", "")} className="px-3 py-2 rounded-md border border-border text-sm text-red-600 hover:bg-red-50 transition-colors">Clear</button>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Prefix</label>
            <select data-testid="field-prefix" value={f.prefix || ""} onChange={(e) => set("prefix", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              {["", "Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Rev.", "Hon."].map((s) => <option key={s} value={s}>{s || "— None —"}</option>)}
            </select>
          </div>
          <Field label="Solicit Code" k="solicit_code" value={f.solicit_code} onChange={set} />
          <Field label="Addressee" k="addressee" value={f.addressee} onChange={set} />
          <Field label="Salutation" k="salutation" value={f.salutation} onChange={set} />
          <Field label="Year Graduated" k="year_graduated" type="number" placeholder="1985" value={f.year_graduated} onChange={set} />
          <Field label="Year Entered" k="year_entered" type="number" placeholder="1981" value={f.year_entered} onChange={set} />
          <Field label="Year Identified" k="class_year" type="number" placeholder="1985" value={f.class_year} onChange={set} />
          <div>
            <label className="block text-sm font-medium mb-1">Living Status</label>
            <select data-testid="field-living_status" value={f.living_status} onChange={(e) => set("living_status", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              <option value="living">Living</option>
              <option value="deceased">Deceased</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Profile Status</label>
            <select data-testid="field-profile_status" value={f.profile_status} onChange={(e) => set("profile_status", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              <option value="unclaimed">Unclaimed</option>
              <option value="active">Active</option>
              <option value="needs_review">Needs Review</option>
            </select>
          </div>
        </div>
      </Card>

      <Repeater title="Emails" items={f.emails} onAdd={() => addItem("emails", { value: "" })} onDel={(i) => delItem("emails", i)} onPrimary={(i) => setPrimary("emails", i)} testid="emails"
        footer={id ? (
          <div className="mt-4 pt-4 border-t border-border space-y-2.5">
            {showLastSent && (
              <div data-testid="last-sent-note" className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Send className="h-3 w-3 text-primary" /> Last sent: <b className="font-medium text-foreground">{showLastSent.title}</b>{showLastSent.date ? ` on ${showLastSent.date}` : ""}
              </div>
            )}
            {!isClassAgent && (
              <div className="space-y-2.5" data-testid="email-prefs">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm">Opt out of <b>all email</b></span>
                  <Switch data-testid="opt-out-all-email" checked={!!f.email_opt_out} onCheckedChange={(v) => requestOptOut({ opt_out_all: v }, v)} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm">Opt out of <b>marketing email</b> <span className="text-muted-foreground font-normal">(newsletters &amp; announcements)</span></span>
                  <Switch data-testid="opt-out-marketing-email" checked={!!f.email_opt_out || (f.email_prefs && f.email_prefs.announcements === false)} disabled={!!f.email_opt_out} onCheckedChange={(v) => requestOptOut({ opt_out_marketing: v }, v)} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm">Opt out of <b>financial / solicitation email</b> <span className="text-muted-foreground font-normal">(fundraising &amp; giving appeals)</span></span>
                  <Switch data-testid="opt-out-solicitation-email" checked={!!f.email_opt_out || (f.email_prefs && f.email_prefs.solicitations === false)} disabled={!!f.email_opt_out} onCheckedChange={(v) => requestOptOut({ opt_out_solicitations: v }, v)} />
                </div>
              </div>
            )}
          </div>
        ) : null}
        render={(it, i) => (
          <>
            <input data-testid={`email-${i}`} value={it.value} onChange={(e) => updItem("emails", i, { value: e.target.value })} placeholder="email@example.com" className="flex-1 px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
            {id && (
              <button type="button" data-testid={`send-comm-${i}`} onClick={() => setSendTo((it.value || "").trim())} disabled={!isEmail(it.value)} title={isEmail(it.value) ? "Send a communication to this address" : "Enter a valid email first"} className="p-2 rounded-md border border-border text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                <Send className="h-4 w-4" />
              </button>
            )}
          </>
        )} />
      {sendTo && <SendCommModal alumnusId={id} email={sendTo} templates={commTemplates} onClose={() => setSendTo(null)} />}
      {research && <ResearchDialog alumnus={f} putBase={base} onClose={() => setResearch(false)} onApplied={(data) => { const loaded = { ...empty, ...data, class_year: data.class_year ?? "", year_graduated: data.year_graduated ?? "", year_entered: data.year_entered ?? "", social: { ...emptySocial, ...(data.social || {}) } }; setF(loaded); savedSnapshot.current = JSON.stringify(loaded); }} />}
      {cautionPatch && <OptOutCautionDialog onCancel={() => setCautionPatch(null)} onConfirm={() => { updateEmailPrefs(cautionPatch); setCautionPatch(null); }} />}

      <Repeater title="Phones" items={f.phones} onAdd={() => addItem("phones", { value: "" })} onDel={(i) => delItem("phones", i)} onPrimary={(i) => setPrimary("phones", i)} testid="phones"
        render={(it, i) => <input data-testid={`phone-${i}`} value={it.value} onChange={(e) => updItem("phones", i, { value: e.target.value })} onBlur={(e) => updItem("phones", i, { value: formatPhone(e.target.value) })} placeholder="(555) 000-0000" className="flex-1 px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />} />

      <Repeater title="Addresses" items={f.addresses} onAdd={() => addItem("addresses", { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" })} onDel={(i) => delItem("addresses", i)} onPrimary={(i) => setPrimary("addresses", i)} testid="addresses"
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-2 gap-2">
            <input value={it.line1} onChange={(e) => updItem("addresses", i, { line1: e.target.value })} placeholder="Street" className="col-span-2 px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.city} onChange={(e) => updItem("addresses", i, { city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.state} onChange={(e) => updItem("addresses", i, { state: e.target.value })} placeholder="State" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.postal_code || ""} onChange={(e) => updItem("addresses", i, { postal_code: e.target.value })} placeholder="ZIP / Postal code" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.country} onChange={(e) => updItem("addresses", i, { country: e.target.value })} placeholder="Country" className="px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )} />

      <Repeater title="Degrees" items={f.degrees} onAdd={() => addItem("degrees", { grad_year: "", degree: "", major: "", minor: "" })} onDel={(i) => delItem("degrees", i)} onPrimary={(i) => setPrimary("degrees", i)} testid="degrees"
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-2 gap-2">
            <input value={it.degree} onChange={(e) => updItem("degrees", i, { degree: e.target.value })} placeholder="Degree (BS, MA)" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.grad_year} onChange={(e) => updItem("degrees", i, { grad_year: e.target.value })} placeholder="Grad Year" type="number" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.major} onChange={(e) => updItem("degrees", i, { major: e.target.value })} placeholder="Major" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.minor} onChange={(e) => updItem("degrees", i, { minor: e.target.value })} placeholder="Minor" className="px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )} />

      <Repeater title="Employment" items={f.employments} onAdd={() => addItem("employments", { employer: "", title: "", industry: "" })} onDel={(i) => delItem("employments", i)} onPrimary={(i) => setPrimary("employments", i)} testid="employments"
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-3 gap-2">
            <input value={it.employer} onChange={(e) => updItem("employments", i, { employer: e.target.value })} placeholder="Employer" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.title} onChange={(e) => updItem("employments", i, { title: e.target.value })} placeholder="Job Title" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.industry} onChange={(e) => updItem("employments", i, { industry: e.target.value })} placeholder="Industry" className="px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )} />

      <Card title="Membership & Interests">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Membership Status</label>
            <select data-testid="field-membership_status" value={f.membership_status} onChange={(e) => set("membership_status", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              <option value="none">None</option>
              <option value="annual">Annual Member</option>
              <option value="lifetime">Lifetime Member</option>
              <option value="honorary">Honorary Member</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Member Since</label>
            <input data-testid="field-membership_since" type="date" value={f.membership_since} onChange={(e) => set("membership_since", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Renewal / Expires</label>
            <input data-testid="field-membership_expires" type="date" value={f.membership_expires} onChange={(e) => set("membership_expires", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">Interests <span className="text-muted-foreground font-normal">(comma separated)</span></label>
          <input data-testid="field-interests" value={f.interests.join(", ")} onChange={(e) => set("interests", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Sailing, Mentoring, Jazz, Volunteering" className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {f.interests.map((it, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground text-xs">{it}</span>)}
          </div>
        </div>
      </Card>

      <Repeater title="Family / Relationships" items={f.family} onAdd={() => addItem("family", { relationship: "Spouse", name: "", notes: "" })} onDel={(i) => delItem("family", i)} testid="family" noPrimary
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-3 gap-2">
            <select value={it.relationship} onChange={(e) => updItem("family", i, { relationship: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background">
              {["Spouse", "Partner", "Child", "Parent", "Sibling", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={it.name} onChange={(e) => updItem("family", i, { name: e.target.value })} placeholder="Name" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.notes} onChange={(e) => updItem("family", i, { notes: e.target.value })} placeholder="Notes" className="px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )} />

      <div data-testid="last-gift-date-readonly" className="mb-3 flex items-center gap-3 text-sm px-4 py-3 rounded-lg bg-card border border-border">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Last Gift Date <span className="normal-case tracking-normal">(from advancement · read-only)</span></span>
        <span className="font-medium ml-auto">{f.last_gift_date || "—"}</span>
      </div>
      <Repeater title="Giving History" items={f.giving} onAdd={() => addItem("giving", { date: "", amount: "", campaign: "", fund: "", notes: "" })} onDel={(i) => delItem("giving", i)} testid="giving" noPrimary
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input type="date" value={it.date} onChange={(e) => updItem("giving", i, { date: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background" />
            <input type="number" value={it.amount} onChange={(e) => updItem("giving", i, { amount: e.target.value })} placeholder="Amount $" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.campaign} onChange={(e) => updItem("giving", i, { campaign: e.target.value })} placeholder="Campaign" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input value={it.fund} onChange={(e) => updItem("giving", i, { fund: e.target.value })} placeholder="Fund" className="px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )} />

      <Repeater title="Event Attendance" items={f.events} onAdd={() => addItem("events", { event_name: "", date: "", role: "Attendee", notes: "" })} onDel={(i) => delItem("events", i)} testid="events" noPrimary
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
            <input value={it.event_name} onChange={(e) => updItem("events", i, { event_name: e.target.value })} placeholder="Event name" className="px-3 py-2 rounded-md border border-input bg-background" />
            <input type="date" value={it.date} onChange={(e) => updItem("events", i, { date: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background" />
            <select value={it.role} onChange={(e) => updItem("events", i, { role: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background">
              {["Attendee", "Speaker", "Volunteer", "Guest", "Host"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )} />

      <Repeater title="Communication History" items={f.communications} onAdd={() => addItem("communications", { date: "", channel: "email", subject: "", notes: "" })} onDel={(i) => delItem("communications", i)} testid="communications" noPrimary
        render={(it, i) => (
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
            <input type="date" value={it.date} onChange={(e) => updItem("communications", i, { date: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background" />
            <select value={it.channel} onChange={(e) => updItem("communications", i, { channel: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background">
              {["email", "sms", "call", "letter", "in_person"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={it.subject} onChange={(e) => updItem("communications", i, { subject: e.target.value })} placeholder="Subject" className="px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )} />

      <Card title="Social Media">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SOCIAL_PLATFORMS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.key}>
                <label className="flex items-center gap-2 text-sm font-medium mb-1"><Icon className="h-4 w-4 text-muted-foreground" /> {p.label}</label>
                <input data-testid={`social-${p.key}`} value={f.social[p.key] || ""} onChange={(e) => setSocial(p.key, e.target.value)} placeholder={p.placeholder}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
              </div>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end mt-6">
        <button onClick={save} disabled={busy} className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Alumnus"}
        </button>
      </div>
    </div>
  );
}

function SendCommModal({ alumnusId, email, templates, onClose, onSent }) {
  const [selId, setSelId] = useState(templates[0]?.id || "");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html }
  const selTitle = templates.find((t) => t.id === selId)?.title || "";

  const doPreview = async () => {
    if (!selId) { toast.error("Pick a communication first"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post(`/alumni/${alumnusId}/communication-preview`, { comm_id: selId, email });
      setPreview({ subject: data.subject, html: data.html });
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (!selId) { toast.error("Pick a communication to send"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/alumni/${alumnusId}/send-communication`, { comm_id: selId, email });
      toast.success(data?.message || "Communication sent");
      onSent && onSent(selTitle);
      onClose();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div data-testid="send-comm-modal" className={`bg-card border border-border rounded-xl shadow-2xl w-full ${preview ? "max-w-2xl" : "max-w-md"} mt-16 mb-10`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-serif text-lg">Send a communication</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-muted-foreground">To: <span className="font-medium text-foreground">{email}</span></div>
          {templates.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">No communication templates found. Create one on the Communications page first.</div>
          ) : preview ? (
            <div className="space-y-3">
              <button data-testid="preview-back" onClick={() => setPreview(null)} className="text-sm text-primary hover:underline flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Back to templates</button>
              <div className="text-sm"><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{preview.subject}</span></div>
              <iframe data-testid="preview-frame" title="Email preview" srcDoc={preview.html} className="w-full h-[420px] rounded-md border border-border bg-white" />
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {templates.map((t) => (
                <label key={t.id} data-testid={`comm-option-${t.id}`} className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${selId === t.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                  <input type="radio" name="comm" value={t.id} checked={selId === t.id} onChange={() => setSelId(t.id)} className="mt-1 accent-current" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{t.title}</div>
                    {t.subject && <div className="text-xs text-muted-foreground truncate">{t.subject}</div>}
                    {t.audience && <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground mt-0.5">{t.audience}</div>}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm border border-border hover:bg-muted">Cancel</button>
          {!preview && (
            <button data-testid="preview-comm" onClick={doPreview} disabled={previewing || !selId || templates.length === 0} className="inline-flex items-center gap-1.5 rounded-md border border-primary text-primary px-4 py-2 text-sm font-medium hover:bg-primary/5 disabled:opacity-60">
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
            </button>
          )}
          <button data-testid="send-comm-confirm" onClick={send} disabled={sending || !selId || templates.length === 0} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-white px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, k, type = "text", placeholder, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input data-testid={`field-${k}`} type={type} value={value ?? ""} onChange={(e) => onChange(k, e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-card border border-border rounded-lg shadow-sm p-5 mb-5">
    <h2 className="font-serif text-xl tracking-tight mb-4">{title}</h2>
    {children}
  </div>
);

const Repeater = ({ title, items, render, onAdd, onDel, onPrimary, testid, noPrimary, footer }) => (
  <Card title={title}>
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={it.id} className="flex items-start gap-2">
          {render(it, i)}
          {!noPrimary && (
            <button type="button" onClick={() => onPrimary(i)} title="Set primary" className={`p-2 rounded-md border transition-colors ${it.primary ? "bg-accent/20 border-accent text-accent-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}>
              <Star className="h-4 w-4" />
            </button>
          )}
          <button type="button" data-testid={`del-${testid}-${i}`} onClick={() => onDel(i)} className="p-2 rounded-md border border-border text-muted-foreground hover:text-destructive hover:bg-muted">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-muted-foreground">None added.</div>}
    </div>
    <button type="button" data-testid={`add-${testid}`} onClick={onAdd} className="mt-3 flex items-center gap-1.5 text-sm text-primary hover:underline">
      <Plus className="h-4 w-4" /> Add {title.replace(/s$/, "")}
    </button>
    {footer}
  </Card>
);
